/**
 * Public vehicle detail service (Milestone 7).
 */
import {
  fetchClientViewVehicleDetail,
} from "../api/clientViewVehicleApi";
import { DEV_SAMPLE_VEHICLE_LISTINGS_RAW } from "../data/devSampleVehicleListings";
import { VEHICLE_DETAIL_PAGE_STATUS, VEHICLE_DETAIL_SIMILAR_LISTINGS_LIMIT } from "../constants/automapVehicleDetailConstants";
import { AUTOMAPA_PUBLIC_VIEW_SLUG } from "../constants/automapVehicleSearchConstants";
import {
  normalizeListingForDetail,
  normalizeVehicleListings,
  getListingPublicStatus,
  shouldShowVehicleDetail,
  shouldShowUnavailableDetailPage,
} from "../utils/automapListing";
import { PUBLIC_STATUS } from "../constants/automapListingConstants";
import { listingMatchesDetailParam } from "../utils/automapListing/listingDetailRoutes";
import { searchPublicVehicleListings } from "./publicVehicleListingsService";
import { getSimilarVehicleListings, getVehicleRecommendationsForListing } from "./similarVehicleListingsService";
import {
  buildPublicCacheKey,
  readMemoryCache,
  writeMemoryCache,
} from "../utils/publicRequestCache";

const SLUG_RESOLVE_CACHE_TTL_MS = 2 * 60 * 1000;

const DEV_FALLBACK_ENABLED =
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_AUTOMAPA_DETAIL_DEV_FALLBACK !== "false";

function logDetailDebug(message, meta = {}) {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug(`[AutoMapa detail] ${message}`, meta);
  }
}

function mergeDealerOntoListing(raw, dealer) {
  if (!raw || !dealer) return raw;
  return {
    ...raw,
    dealerVerificationStatus: dealer.verificationStatus ?? raw.dealerVerificationStatus,
    dealerStatus: dealer.status ?? raw.dealerStatus,
    dealerPublicProfileEnabled: dealer.publicProfileEnabled ?? raw.dealerPublicProfileEnabled,
    dealerVerified: dealer.dealerVerified ?? raw.dealerVerified,
    dealerName: raw.dealerName || dealer.name,
    dealerSlug: raw.dealerSlug || dealer.slug,
    dealerWhatsapp: raw.dealerWhatsapp || dealer.whatsapp,
    dealerPhone: raw.dealerPhone || dealer.phone,
    dealerCity: raw.dealerCity || dealer.city,
    dealerLogoUrl: raw.dealerLogoUrl || dealer.logoUrl,
    dealerId: raw.dealerId || dealer.id,
  };
}

function normalizeDealer(dealer) {
  if (!dealer) return null;
  return {
    id: dealer.id || null,
    name: dealer.name || null,
    slug: dealer.slug || null,
    logoUrl: dealer.logoUrl || null,
    dealerVerified: Boolean(dealer.dealerVerified),
    verificationStatus: dealer.verificationStatus || "unknown",
    status: dealer.status || null,
    city: dealer.city || null,
    neighborhood: dealer.neighborhood || null,
    whatsapp: dealer.whatsapp || null,
    phone: dealer.phone || null,
    responseTimeLabel: dealer.responseTimeLabel || null,
    publicProfileEnabled: dealer.publicProfileEnabled,
  };
}

function resolveDetailPageStatus(listing, { apiUnavailable = false } = {}) {
  if (!listing?.id) return VEHICLE_DETAIL_PAGE_STATUS.NOT_FOUND;
  const publicStatus = getListingPublicStatus(listing);

  if (publicStatus === PUBLIC_STATUS.HIDDEN || publicStatus === PUBLIC_STATUS.NOT_FOUND) {
    return VEHICLE_DETAIL_PAGE_STATUS.NOT_FOUND;
  }
  if (apiUnavailable || publicStatus === PUBLIC_STATUS.UNAVAILABLE) {
    return VEHICLE_DETAIL_PAGE_STATUS.UNAVAILABLE;
  }
  if (publicStatus === PUBLIC_STATUS.VISIBLE) {
    return VEHICLE_DETAIL_PAGE_STATUS.READY;
  }
  return VEHICLE_DETAIL_PAGE_STATUS.NOT_FOUND;
}

function processApiDetailBody(body) {
  const raw = body?.vehicle || null;
  if (!raw) return null;

  const mergedRaw = mergeDealerOntoListing(raw, body.dealer);
  const listing = normalizeListingForDetail(mergedRaw);
  listing.publicStatus = getListingPublicStatus(listing);

  return {
    listing,
    dealer: normalizeDealer(body.dealer),
    featuredPlacement: body.featuredPlacement || null,
    apiUnavailable: Boolean(body.unavailable),
    unavailableMessage: body.unavailableMessage || null,
    warnings: [],
    source: body.isDealerManaged ? "dealer_managed" : "api",
  };
}

async function resolveListingIdFromSlug(slug, param, profileId) {
  const cacheKey = buildPublicCacheKey(["slug-resolve", slug, param, profileId || ""]);
  const cached = readMemoryCache(cacheKey, SLUG_RESOLVE_CACHE_TTL_MS);
  if (cached !== null) return cached;

  const result = await searchPublicVehicleListings(
    slug,
    { keyword: param, page: 1, pageSize: 48 },
    { profileId, allowDevFallback: true }
  );
  if (!result.ok) {
    writeMemoryCache(cacheKey, null);
    return null;
  }
  const match = result.listings.find((listing) => listingMatchesDetailParam(listing, param));
  const resolvedId = match?.id || null;
  writeMemoryCache(cacheKey, resolvedId);
  return resolvedId;
}

async function fetchDetailBody(slug, param, { profileId } = {}) {
  const params = profileId ? { profile: profileId } : {};

  try {
    const direct = await fetchClientViewVehicleDetail(slug, param, params);
    if (direct?.vehicle) return direct;
  } catch (error) {
    logDetailDebug("direct detail fetch failed", { param, message: error?.message });
  }

  const resolvedId = await resolveListingIdFromSlug(slug, param, profileId);
  if (resolvedId && resolvedId !== param) {
    const byId = await fetchClientViewVehicleDetail(slug, resolvedId, params);
    if (byId?.vehicle) return byId;
  }

  return null;
}

function getDevDetailBySlugOrId(param) {
  const raw = DEV_SAMPLE_VEHICLE_LISTINGS_RAW.find((entry) => {
    const listing = normalizeVehicleListings([entry])[0];
    return listing && listingMatchesDetailParam(listing, param);
  });
  if (!raw) return null;

  const listing = normalizeListingForDetail(raw);
  listing.publicStatus = getListingPublicStatus(listing);

  return {
    listing,
    dealer: listing.dealerName
      ? {
          id: listing.dealerId,
          name: listing.dealerName,
          slug: listing.dealerSlug,
          dealerVerified: listing.dealerVerified,
          verificationStatus: listing.dealerVerificationStatus,
          whatsapp: listing.dealerWhatsapp,
          city: listing.dealerCity || listing.city,
        }
      : null,
    featuredPlacement: null,
    apiUnavailable: shouldShowUnavailableDetailPage(listing),
    unavailableMessage: "Este vehículo ya no está disponible.",
    warnings: ["dev_fallback"],
    source: "dev_fallback",
  };
}

/**
 * Load recommendation rail data without blocking main detail fetch (M17).
 */
export async function loadVehicleListingRecommendations(
  slug = AUTOMAPA_PUBLIC_VIEW_SLUG,
  listing,
  options = {}
) {
  const similarLimit = options.limit ?? options.similarLimit ?? VEHICLE_DETAIL_SIMILAR_LISTINGS_LIMIT;

  if (!listing?.id) {
    return {
      similarListings: [],
      recommendationSections: [],
      recommendationSearchHref: null,
      recommendationWarnings: ["missing_listing"],
    };
  }

  try {
    const recommendationResult = await getVehicleRecommendationsForListing(slug, listing, {
      profileId: options.profileId,
      limit: similarLimit,
      dealer: options.dealer,
      slug,
    });

    return {
      similarListings: recommendationResult.allRecommendations.slice(0, similarLimit),
      recommendationSections: recommendationResult.sections,
      recommendationSearchHref: recommendationResult.searchHref,
      recommendationWarnings: recommendationResult.warnings,
    };
  } catch {
    return {
      similarListings: [],
      recommendationSections: [],
      recommendationSearchHref: null,
      recommendationWarnings: ["fetch_failed"],
    };
  }
}

/**
 * @param {string} slug
 * @param {string} slugOrId
 * @param {{ profileId?: string, allowDevFallback?: boolean, similarLimit?: number, includeRecommendations?: boolean }} [options]
 */
export async function getVehicleListingDetail(
  slug = AUTOMAPA_PUBLIC_VIEW_SLUG,
  slugOrId,
  options = {}
) {
  const allowDevFallback = options.allowDevFallback ?? DEV_FALLBACK_ENABLED;
  const profileId = options.profileId;
  const similarLimit = options.similarLimit ?? VEHICLE_DETAIL_SIMILAR_LISTINGS_LIMIT;
  const includeRecommendations = options.includeRecommendations !== false;

  if (!slugOrId) {
    return {
      status: VEHICLE_DETAIL_PAGE_STATUS.NOT_FOUND,
      listing: null,
      dealer: null,
      similarListings: [],
      featuredPlacement: null,
      warnings: [],
      message: "No encontramos este vehículo.",
      source: null,
    };
  }

  try {
    let processed = null;
    const body = await fetchDetailBody(slug, slugOrId, { profileId });
    if (body) {
      processed = processApiDetailBody(body);
    } else if (allowDevFallback) {
      processed = getDevDetailBySlugOrId(slugOrId);
      logDetailDebug("using development fallback detail", { param: slugOrId });
    }

    if (!processed?.listing) {
      return {
        status: VEHICLE_DETAIL_PAGE_STATUS.NOT_FOUND,
        listing: null,
        dealer: null,
        similarListings: [],
        featuredPlacement: null,
        warnings: processed?.warnings || [],
        message: "No encontramos este vehículo.",
        source: processed?.source || null,
      };
    }

    const status = resolveDetailPageStatus(processed.listing, {
      apiUnavailable: processed.apiUnavailable,
    });

    if (!shouldShowVehicleDetail(processed.listing) && status === VEHICLE_DETAIL_PAGE_STATUS.NOT_FOUND) {
      return {
        status: VEHICLE_DETAIL_PAGE_STATUS.NOT_FOUND,
        listing: null,
        dealer: null,
        similarListings: [],
        featuredPlacement: null,
        warnings: processed.warnings,
        message: "No encontramos este vehículo.",
        source: processed.source,
      };
    }

    const recommendationResult =
      includeRecommendations &&
      (status === VEHICLE_DETAIL_PAGE_STATUS.READY ||
        status === VEHICLE_DETAIL_PAGE_STATUS.UNAVAILABLE)
        ? await getVehicleRecommendationsForListing(slug, processed.listing, {
            profileId,
            limit: similarLimit,
            dealer: processed.dealer,
            slug,
          })
        : { sections: [], allRecommendations: [], warnings: [] };

    const similarListings = recommendationResult.allRecommendations.slice(0, similarLimit);

    return {
      status,
      listing: processed.listing,
      dealer: processed.dealer,
      similarListings,
      recommendationSections: recommendationResult.sections,
      recommendationSearchHref: recommendationResult.searchHref,
      recommendationWarnings: recommendationResult.warnings,
      featuredPlacement: processed.featuredPlacement,
      warnings: processed.warnings,
      message:
        status === VEHICLE_DETAIL_PAGE_STATUS.UNAVAILABLE
          ? processed.unavailableMessage || "Este vehículo ya no está disponible."
          : null,
      source: processed.source,
    };
  } catch (error) {
    logDetailDebug("detail fetch error", { message: error?.message });

    if (allowDevFallback) {
      const processed = getDevDetailBySlugOrId(slugOrId);
      if (processed?.listing) {
        const status = resolveDetailPageStatus(processed.listing, {
          apiUnavailable: processed.apiUnavailable,
        });
        const recommendationResult = includeRecommendations
          ? await getVehicleRecommendationsForListing(slug, processed.listing, {
              profileId,
              limit: similarLimit,
              dealer: processed.dealer,
              slug,
            })
          : { sections: [], allRecommendations: [], warnings: [] };
        const similarListings = recommendationResult.allRecommendations.slice(0, similarLimit);
        return {
          status,
          listing: processed.listing,
          dealer: processed.dealer,
          similarListings,
          recommendationSections: recommendationResult.sections,
          recommendationSearchHref: recommendationResult.searchHref,
          recommendationWarnings: recommendationResult.warnings,
          featuredPlacement: null,
          warnings: ["dev_fallback"],
          message:
            status === VEHICLE_DETAIL_PAGE_STATUS.UNAVAILABLE
              ? processed.unavailableMessage
              : null,
          source: "dev_fallback",
        };
      }
    }

    return {
      status: VEHICLE_DETAIL_PAGE_STATUS.ERROR,
      listing: null,
      dealer: null,
      similarListings: [],
      featuredPlacement: null,
      warnings: [],
      message: error?.message || "No pudimos cargar este vehículo.",
      source: null,
    };
  }
}

/** @deprecated use getVehicleListingDetail */
export const getPublicVehicleListingBySlug = getVehicleListingDetail;
